import { useState, useRef, KeyboardEvent, useEffect } from 'react';
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Box,
  Typography,
  Autocomplete,
  Grid2,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
  Link
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { usePlaces } from '../hooks/usePlaces';
import { useSaleListings } from '../hooks/useSaleListings';

interface AddHomeFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (homeName: string, address: string, details: {
    bedrooms: number;
    bathrooms: number;
    sqft: number;
    price: number;
  }) => Promise<void>;
  isSubmitting: boolean;
  isEdit?: boolean;
  initialValues?: {
    name: string;
    address: string;
    bedrooms?: number;
    bathrooms?: number;
    sqft?: number;
    price?: number;
  };
  title?: string;
  submitButtonText?: string;
}

const AddHomeForm: React.FC<AddHomeFormProps> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  isEdit = false,
  initialValues = { name: '', address: '', bedrooms: 3, bathrooms: 2, sqft: 1800, price: 350000 },
  title = 'Add New Home',
  submitButtonText = 'Add Home'
}) => {
  const [homeName, setHomeName] = useState(initialValues.name);
  const [address, setAddress] = useState('');
  const [bedrooms, setBedrooms] = useState(initialValues.bedrooms || 3);
  const [bathrooms, setBathrooms] = useState(initialValues.bathrooms || 2);
  const [sqft, setSqft] = useState(initialValues.sqft || 1800);
  const [price, setPrice] = useState(initialValues.price || 350000);
  
  const nameFieldRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Use the places hook for address autocompletion
  const { 
    suggestions, 
    loading: isLoadingAddresses, 
    error,
    getFullAddress 
  } = usePlaces(address);

  // Use the sale listings hook for auto-filling property details
  const { 
    isLoading: isLoadingSaleData, 
    error: saleDataError, 
    fetchSaleListingsByAddress 
  } = useSaleListings();
  
  // Parse address from details JSON when in edit mode
  useEffect(() => {
    if (open && isEdit) {
      setHomeName(initialValues.name);
      setBedrooms(initialValues.bedrooms || 3);
      setBathrooms(initialValues.bathrooms || 2);
      setSqft(initialValues.sqft || 1800);
      setPrice(initialValues.price || 350000);
      
      // Parse the address from details JSON if needed
      try {
        if (initialValues.address) {
          // Check if it's a JSON string
          if (initialValues.address.startsWith('{')) {
            const detailsObj = JSON.parse(initialValues.address);
            setAddress(detailsObj.address || '');
          } else {
            // Fallback for backward compatibility
            setAddress(initialValues.address);
          }
        } else {
          setAddress('');
        }
      } catch (e) {
        // If parsing fails, use the raw value
        console.error('Failed to parse details JSON:', e);
        setAddress(initialValues.address);
      }
    }
  }, [open, isEdit, initialValues]);
  
  // Reset form when closed
  useEffect(() => {
    if (!open && !isEdit) {
      setHomeName('');
      setAddress('');
      setBedrooms(3);
      setBathrooms(2);
      setSqft(1800);
      setPrice(350000);
    }
  }, [open, isEdit]);

  // Handle address change
  const handleAddressInputChange = (_: React.SyntheticEvent, newInputValue: string) => {
    setAddress(newInputValue);
  };
  
  // Handle suggestion selection
  const handleAddressSelection = async (_: React.SyntheticEvent, value: any) => {
    if (value && typeof value === 'object') {
      // Cancel any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // When a suggestion is selected, use the full address from the prediction
      const selectedAddress = getFullAddress(value);
      setAddress(selectedAddress);
      
      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();
      
      // Fetch sale listings data to auto-fill property details
      try {
        const saleData = await fetchSaleListingsByAddress(selectedAddress, abortControllerRef.current.signal);
        if (saleData) {
          // Auto-fill property details without confirmation
          setBedrooms(saleData.bedrooms);
          setBathrooms(saleData.bathrooms);
          setSqft(saleData.sqft);
          setPrice(saleData.price);
        }
      } catch (error) {
        // Sale data fetch failed, but continue with address selection
        // Don't log aborted requests as errors
        if (error instanceof Error && error.name !== 'AbortError') {
          console.warn('Failed to fetch sale listings for auto-fill:', error);
        }
      }
    }
  };
  
  const handleSubmit = () => {
    if (homeName.trim()) {
      // Pass name, address, and property details to the parent component
      onSubmit(homeName, address, {
        bedrooms,
        bathrooms,
        sqft,
        price
      });
    }
  };
  
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !isSubmitting && homeName.trim()) {
      handleSubmit();
    }
  };
  
  const handleClose = () => {
    // Cancel any pending sale listing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    if (!isEdit) {
      setHomeName('');
      setAddress('');
      setBedrooms(3);
      setBathrooms(2);
      setSqft(1800);
    }
    onClose();
  };
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Add these handlers for increment/decrement buttons
  const incrementBedrooms = () => setBedrooms(prev => Math.min(20, prev + 1));
  const decrementBedrooms = () => setBedrooms(prev => Math.max(1, prev - 1));
  
  const incrementBathrooms = () => setBathrooms(prev => Math.min(10, prev + 0.5));
  const decrementBathrooms = () => setBathrooms(prev => Math.max(0.5, prev - 0.5));
  
  const incrementSqft = () => setSqft(prev => Math.min(50000, prev + 100));
  const decrementSqft = () => setSqft(prev => Math.max(100, prev - 100));
  
  // Add increment/decrement handlers for price
  const incrementPrice = () => setPrice(prev => Math.min(10000000, prev + 5000));
  const decrementPrice = () => setPrice(prev => Math.max(10000, prev - 5000));
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx ={{ zIndex: 3001 }}
      slotProps={{
        transition: {
          onEntered: () => nameFieldRef.current?.focus()
        }
      }}
      >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Box sx={{ py: 0, mt: 0 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
            Property Details
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              inputRef={nameFieldRef}
              autoFocus
              label="Property Name"
              type="text"
              fullWidth
              variant="outlined"
              value={homeName}
              disabled={isSubmitting}
              onChange={(e) => setHomeName(e.target.value)}
              onKeyDown={handleKeyDown}
              required
              helperText="Give your property a name to identify it"
              margin="dense"
              InputLabelProps={{
                style: { 
                  zIndex: 1,
                  background: 'transparent'
                }
              }}
              sx={{ 
                '& .MuiInputBase-root': { transform: 'translate(0px, 5px)' },
                '& .MuiFormHelperText-root': { transform: 'translate(0px, 5px)' },
              }}
            />
            <Autocomplete
              freeSolo
              options={suggestions}
              inputValue={address}
              onInputChange={handleAddressInputChange}
              onChange={handleAddressSelection}
              loading={isLoadingAddresses}
              slotProps={{
                popper: {
                  sx: {
                    zIndex: 3002,
                  },
                },
              }}
              
              getOptionLabel={(option) => {
                // Handle both string options and prediction objects
                if (typeof option === 'string') {
                  return option;
                }
                // Use the full address from the prediction
                return getFullAddress(option);
              }}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    {getFullAddress(option)}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Property Address"
                  variant="outlined"
                  fullWidth
                  disabled={isSubmitting}
                  helperText="Enter the complete address of the property"
                  margin="dense"
                  InputLabelProps={{
                    style: { 
                      zIndex: 1,
                      background: 'transparent'
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {(isLoadingAddresses || isLoadingSaleData) && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{ 
                    '& .MuiInputBase-root': { transform: 'translate(0px, 5px)' },
                    '& .MuiFormHelperText-root': { transform: 'translate(0px, 5px)' },
                  }}
                />
              )}
            />
            {error && (
              <Typography color="error" variant="caption">
                Error loading address suggestions: {error}
              </Typography>
            )}
            {saleDataError && (
              <Typography sx={{ color: 'warning.main' }} variant="caption">
                Could not load property details for auto-fill: {saleDataError}
              </Typography>
            )}
            
            {/* Property specifications */}
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 0.5, fontWeight: 500 }}>
              Property Specifications
            </Typography>
            
            <Grid2 container spacing={2}>
              <Grid2 size={{xs: 12, sm:4 }}>
                {isMobile ? (
                  // Mobile layout with buttons beside input
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      label="Bedrooms"
                      type="number"
                      fullWidth
                      variant="outlined"
                      value={bedrooms}
                      disabled={isSubmitting}
                      onChange={(e) => setBedrooms(Math.max(1, parseInt(e.target.value) || 1))}
                      inputProps={{ min: 1, max: 20 }}
                      margin="dense"
                      InputLabelProps={{
                        style: { zIndex: 1, background: 'transparent' }
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                      <IconButton 
                        onClick={incrementBedrooms} 
                        size="small"
                        disabled={bedrooms >= 20 || isSubmitting}
                        sx={{ minWidth: 36, height: 24 }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        onClick={decrementBedrooms} 
                        size="small"
                        disabled={bedrooms <= 1 || isSubmitting}
                        sx={{ minWidth: 36, height: 24 }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  // Desktop layout (unchanged)
                  <TextField
                    label="Bedrooms"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={bedrooms}
                    disabled={isSubmitting}
                    onChange={(e) => setBedrooms(Math.max(1, parseInt(e.target.value) || 1))}
                    inputProps={{ min: 1, max: 20 }}
                    margin="dense"
                    InputLabelProps={{
                      style: { zIndex: 1, background: 'transparent' }
                    }}
                  />
                )}
              </Grid2>
              
              <Grid2 size={{xs:12, sm: 4}}>
                {isMobile ? (
                  // Mobile layout with buttons beside input
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      label="Bathrooms"
                      type="number"
                      fullWidth
                      variant="outlined"
                      value={bathrooms}
                      disabled={isSubmitting}
                      onChange={(e) => setBathrooms(Math.max(0.5, parseFloat(e.target.value) || 0.5))}
                      inputProps={{ min: 0.5, max: 10, step: 0.5 }}
                      margin="dense"
                      InputLabelProps={{
                        style: { zIndex: 1, background: 'transparent' }
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                      <IconButton 
                        onClick={incrementBathrooms} 
                        size="small"
                        disabled={bathrooms >= 10 || isSubmitting}
                        sx={{ minWidth: 36, height: 24 }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        onClick={decrementBathrooms} 
                        size="small"
                        disabled={bathrooms <= 0.5 || isSubmitting}
                        sx={{ minWidth: 36, height: 24 }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  // Desktop layout (unchanged)
                  <TextField
                    label="Bathrooms"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={bathrooms}
                    disabled={isSubmitting}
                    onChange={(e) => setBathrooms(Math.max(0.5, parseFloat(e.target.value) || 0.5))}
                    inputProps={{ min: 0.5, max: 10, step: 0.5 }}
                    margin="dense"
                    InputLabelProps={{
                      style: { zIndex: 1, background: 'transparent' }
                    }}
                  />
                )}
              </Grid2>
              
              <Grid2 size={{xs: 12, sm:4 }}>
                {isMobile ? (
                  // Mobile layout with buttons beside input
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      label="Square Feet"
                      type="number"
                      fullWidth
                      variant="outlined"
                      value={sqft}
                      disabled={isSubmitting}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty or partial input during typing
                        if (value === '' || value === null) {
                          setSqft(0); // Temporarily allow zero
                        } else {
                          const parsed = parseInt(value);
                          setSqft(isNaN(parsed) ? 0 : parsed);
                        }
                      }}
                      inputProps={{ 
                        min: 100,
                        max: 50000,
                        inputMode: 'numeric' // This triggers the numeric keypad on mobile
                      }}
                      margin="dense"
                      InputLabelProps={{
                        style: { zIndex: 1, background: 'transparent' }
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                      <IconButton 
                        onClick={incrementSqft} 
                        size="small"
                        disabled={sqft >= 50000 || isSubmitting}
                        sx={{ minWidth: 36, height: 24 }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        onClick={decrementSqft} 
                        size="small"
                        disabled={sqft <= 100 || isSubmitting}
                        sx={{ minWidth: 36, height: 24 }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  // Desktop layout (unchanged)
                  <TextField
                    label="Square Feet"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={sqft}
                    disabled={isSubmitting}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty or partial input during typing
                      if (value === '' || value === null) {
                        setSqft(0); // Temporarily allow zero
                      } else {
                        const parsed = parseInt(value);
                        setSqft(isNaN(parsed) ? 0 : parsed);
                      }
                    }}
                    inputProps={{ 
                      min: 100, 
                      max: 50000,
                      inputMode: 'numeric' // Also add to desktop for consistency
                    }}
                    margin="dense"
                    InputLabelProps={{
                      style: { zIndex: 1, background: 'transparent' }
                    }}
                  />
                )}
              </Grid2>
              <Grid2 size={{xs: 12, sm:6 }}>
                {isMobile ? (
                  // Mobile layout with buttons beside input
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      label="Price ($)"
                      type="number"
                      fullWidth
                      variant="outlined"
                      value={price}
                      disabled={isSubmitting}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty or partial input during typing
                        if (value === '' || value === null) {
                          setPrice(0); // Temporarily allow zero
                        } else {
                          const parsed = parseInt(value);
                          setPrice(isNaN(parsed) ? 0 : parsed);
                        }
                      }}
                      inputProps={{ 
                        min: 10000,
                        max: 10000000,
                        inputMode: 'numeric',
                        step: 1000
                      }}
                      margin="dense"
                      InputLabelProps={{
                        style: { zIndex: 1, background: 'transparent' }
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                      <IconButton 
                        onClick={incrementPrice} 
                        size="small"
                        disabled={price >= 10000000 || isSubmitting}
                        sx={{ minWidth: 36, height: 24 }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        onClick={decrementPrice} 
                        size="small"
                        disabled={price <= 10000 || isSubmitting}
                        sx={{ minWidth: 36, height: 24 }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  // Desktop layout
                  <TextField
                    label="Price ($)"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={price}
                    disabled={isSubmitting}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || value === null) {
                        setPrice(0);
                      } else {
                        const parsed = parseInt(value);
                        setPrice(isNaN(parsed) ? 0 : parsed);
                      }
                    }}
                    inputProps={{ 
                      min: 10000, 
                      max: 10000000,
                      inputMode: 'numeric',
                      step: 1000
                    }}
                    margin="dense"
                    InputLabelProps={{
                      style: { zIndex: 1, background: 'transparent' }
                    }}
                  />
                )}
              </Grid2>
            </Grid2>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', gap: 2 }}>
        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
          <Button 
            onClick={handleClose} 
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={!homeName.trim() || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
          >
            {isSubmitting ? (isEdit ? 'Updating...' : 'Adding...') : submitButtonText}
          </Button>
        </Box>
        
        {/* Privacy and Terms Notice Footer */}
        <Box sx={{ width: '100%', pt: 1 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 1, color: 'text.secondary' }}>
            By creating this home you're agreeing to our
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, alignItems: 'center' }}>
            <Link 
              href="https://cribcritic.ai/privacy-policy" 
              target="_blank" 
              rel="noopener noreferrer"
              variant="caption"
              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Privacy Policy
            </Link>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              •
            </Typography>
            <Link 
              href="https://cribcritic.ai/terms-of-service" 
              target="_blank" 
              rel="noopener noreferrer"
              variant="caption"
              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Terms of Service
            </Link>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AddHomeForm;